import { Card, CardContent, Typography } from "@mui/material";
import { format } from "d3-format";
import { Fragment } from "react";
import {
  Area,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import { perspectiveLabels } from "../../lib/diagramTypes";
import { perspective_colors } from "../../lib/diagramTypes";

const CustomTooltip = ({ active, payload, label, data }) => {
  if (active && payload && payload.length) {
    const payload_data = data.filter((o) => o.name === label)[0];
    const filtered_payload = payload
      .filter((item) => !new RegExp(/.+-detail-\d+/).test(item.name))
      .filter((item) => !new RegExp(/.+-stdev-values/).test(item.name));
    return (
      <Card>
        <CardContent>
          <Typography variant="subtitle2" component="span">
            {label}
          </Typography>
          {filtered_payload.map((item, i) => {
            const filtered_keys = Object.keys(payload_data).filter(
              (key) =>
                key.includes(item.name) &&
                new RegExp(/.+-\d/).test(key) &&
                payload_data[key]
            );

            const n = filtered_keys.length;
            const sd = payload_data[`${item.name}-stdev`];

            if (n > 1 && sd !== null) {
              return (
                <Typography key={i} variant="body2" sx={{ color: item.color }}>
                  {perspectiveLabels.get(item.name)}
                  <br />
                  <span className="italic">M:</span>{" "}
                  {format(".3~r")(item.value)}
                  <br />
                  <span className="italic">SD:</span> {format(".3~r")(sd)}
                  <br />
                  <span className="italic">n:</span> {n}
                </Typography>
              );
            }
            return (
              <Typography key={i} variant="body2" sx={{ color: item.color }}>
                {perspectiveLabels.get(item.name)}: {item.value}
              </Typography>
            );
          })}
        </CardContent>
      </Card>
    );
  }

  return null;
};

export default function LineChart({
  evaluationType,
  data,
  displayedPerspectives,
  all_perspectives,
  displayQuestionDetails,
  displaySubdimensionDetails,
}) {
  const countSurveys = (data, searchStrings) => {
    let count = 0;
    for (const [key, value] of Object.entries(data)) {
      if (searchStrings.some((v) => key.includes(v) && value !== null)) {
        count++;
      }
    }
    return count;
  };

  return (
    <ResponsiveContainer
      width="100%"
      height={evaluationType === "question" ? 700 : 300}
    >
      <ComposedChart
        layout="vertical"
        width={1000}
        height={300}
        data={data}
        margin={{
          top: 30,
          right: 25,
          left: 110,
          bottom: 30,
        }}
      >
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis type="number" domain={[0.5, 4.5]} ticks={[1, 2, 3, 4]} />
        <YAxis
          dataKey="name"
          type="category"
          style={{ cursor: "pointer" }}
          onClick={({ value }) => {
            if (evaluationType === "question") displayQuestionDetails(value);
            if (evaluationType === "subdimension")
              displaySubdimensionDetails(value);
          }}
        />
        <Legend
          payload={all_perspectives.map((perspective) => ({
            inactive: !displayedPerspectives.includes(perspective),
            dataKey: perspective,
            type: "line",
            color: perspective_colors[perspective],
            value: perspectiveLabels.get(perspective),
          }))}
        />
        {all_perspectives.map((p, i) => (
          <Fragment key={p}>
            <Tooltip
              content={
                <CustomTooltip
                  data={data}
                  nrOfSurveys={countSurveys(
                    data[0],
                    p === "Schüler*innen"
                      ? ["Schüler*innen-detail"]
                      : ["Lehrer*innen-detail", "Beobachter*innen-detail"]
                  )}
                />
              }
            />
            <Line
              key={p}
              dataKey={p}
              stroke={perspective_colors[p]}
              strokeWidth={5}
              hide={!displayedPerspectives.includes(p)}
              dot={false}
              strokeLinejoin="round"
            />
            {displayedPerspectives.length === 1
              ? displayedPerspectives.map((p) => (
                  <Area
                    key={`${p}-stdev-values`}
                    dataKey={`${p}-stdev-values`}
                    fill={perspective_colors[p]}
                    stroke={"none"}
                    opacity={0.25}
                  />
                ))
              : ""}
          </Fragment>
        ))}
      </ComposedChart>
    </ResponsiveContainer>
  );
}
